import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import axios from 'axios'
import { extractErrorMessage } from '../utils/apitools'
import { isThemeAIRunCurrent } from '../utils/themeAIStageProgress'
import { estimateThemeAICost, ThemeAICostEstimate } from '../utils/themeAICost'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import {
  BillingDisplayUnit,
  ThemeAIJunkDecision,
  ThemeAITask,
  ThemeAIRun,
  ThemeAIResponseScope,
  CreateThemeAIRunRequest,
  ThemeAIEstimateResponse,
  ThemeAIRunResultsResponse,
  ThemeAIRunRecordsPage,
  WriteThemeAITaskVariableRequest,
  WriteThemeAIVariableResponse,
} from '../types/gotypes'

// Result of a write-variable submit: 'conflict' is a distinct signal (not a
// snackbar error) so the caller can drive the double-confirm overwrite flow
// (spec §12.4) instead of just reporting failure.
export type WriteThemeAIVariableResult =
  | { status: 'ok'; data: WriteThemeAIVariableResponse }
  | { status: 'conflict' }

export interface CreateThemeAITestRunRequest {
  code_frame_id?: string
  code_frame_version?: number
  sample_size: number
  include_explanations?: boolean
  include_sentiment?: boolean
  include_emotion?: boolean
  /** How many responses the code-frame agent read, so the test avoids them. */
  agent_sample_size?: number
}

export interface ThemeAITestRunResponse {
  run: ThemeAIRun
  results: ThemeAIRunResultsResponse
  records: unknown[]
  // true when the sample coded here does not overlap the code-frame agent's
  // sample (themeai-ui-pass T5) — false when the pool was too small to avoid
  // overlap and the fallback drew from the whole pool.
  fresh_sample?: boolean
}

// A test run's mutation result. A 504 from the interactive queue ("did not
// answer in time") is NOT a failure — the run row exists server-side and the
// runs poll below will land its final status. That case comes back as
// `{ status: 'pending' }` instead of a thrown error.
export type CreateThemeAITestRunResult =
  | ({ status: 'completed' } & ThemeAITestRunResponse)
  | { status: 'pending' }

// True only for a 404. Mixed-version deployments can briefly serve the task
// UI before every ThemeAI results endpoint is available.
const isNotAvailableYet = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404

// True for a 504 — the interactive queue's own "still on the queue" signal
// (goapi's themeAIInteractiveError), not a real failure.
export const isThemeAITestRunTimeout = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 504

// Which run's results to fetch: an explicitly-selected run wins. Otherwise
// use the newest production run, never a sampled preview.
export const resolveResultsRunNumber = (
  explicit: number | undefined,
  runs: ThemeAIRun[] | undefined,
): number | undefined => explicit ?? runs?.find((run) => !run.is_test)?.run_number

// A run whose coding is still in flight — used to decide whether to keep
// polling the run list so status/cost/token totals update on completion.
export const isRunInFlight = (run: ThemeAIRun): boolean =>
  run.status === 'pending' || run.status === 'running'

// Newest current run of the given kind (test or production), per the API
// contract's "current run" rule (see isThemeAIRunCurrent).
export const resolveLatestCurrentRun = (
  runs: ThemeAIRun[] | undefined,
  task: ThemeAITask | undefined,
  isTest: boolean,
): ThemeAIRun | undefined => (runs || [])
  .filter((run) => run.is_test === isTest && isThemeAIRunCurrent(task, run))
  .sort((a, b) => b.run_number - a.run_number)[0]

// A run created (create+execute split) but never started — resumable via
// handleResumePendingRun, discardable via handleDiscardPendingRun, and the
// only kind DELETE /runs/:runNumber will accept (status='pending' AND
// phase=''). Restricted to the task's current context so a stale-revision
// stub can't be silently resumed into a 409.
export const resolveNeverStartedPendingRun = (
  runs: ThemeAIRun[] | undefined,
  task: ThemeAITask | undefined,
): ThemeAIRun | undefined => (runs || [])
  .filter((run) => !run.is_test && run.status === 'pending' && !run.phase && isThemeAIRunCurrent(task, run))
  .sort((a, b) => b.run_number - a.run_number)[0]

export interface ThemeAIEstimate {
  responseCount: number
  wordCount: number
  estimatedTokens: number
  estimatedCost: number
  remainingBudget: number | null
  humanComparisonText: string
  /** What coding these responses by hand would cost, in £ (spec #916 §7.2). */
  humanCostGBP?: number
  /** Working days a human coder would take. */
  humanDays?: number
  /** How the customer reads money (spend control PR 6d); credits at £1 a credit on the fallback estimate. */
  moneyUnit?: { display_unit: BillingDisplayUnit; credit_value_gbp: number }
  /** A fixed-mode list price's basis, "11 × 100 responses"; absent for a metered figure. */
  priceBasis?: string
  /** Answers a saved junk decision keeps out of this estimate (dropped, or coded as junk without the model). */
  junkExcluded?: number
  junkDecision?: ThemeAIJunkDecision
  /** True when the server estimate errored and this is the client-side fallback. */
  isFallback: boolean
}

/** The default markup (types.DefaultMarkupPct on the server), for the fallback estimate only. */
export const FALLBACK_MARKUP_PCT = 50

export type ThemeAIEstimateQueryResult =
  | { isFallback: false; data: ThemeAIEstimateResponse }
  | { isFallback: true; data: ThemeAICostEstimate }

// Pure normalisation of either estimate shape into one camelCase view — kept
// free of the query so it's directly unit-testable.
export const normalizeThemeAIEstimate = (result: ThemeAIEstimateQueryResult): ThemeAIEstimate => {
  if (!result.isFallback) {
    const d = result.data
    return {
      responseCount: d.response_count,
      wordCount: d.word_count,
      estimatedTokens: d.estimated_tokens,
      estimatedCost: d.estimated_cost,
      remainingBudget: d.remaining_budget,
      humanComparisonText: d.human_comparison,
      humanCostGBP: d.human_cost_gbp,
      humanDays: d.human_days,
      moneyUnit: d.display_unit ? { display_unit: d.display_unit, credit_value_gbp: d.credit_value_gbp } : undefined,
      priceBasis: d.units ? `${d.units.toLocaleString('en-GB')} × ${d.unit_label ?? 'unit'}` : undefined,
      junkExcluded: d.junk_excluded,
      junkDecision: d.junk_decision,
      isFallback: false,
    }
  }
  const d = result.data
  return {
    responseCount: d.responseCount,
    wordCount: d.wordCount,
    estimatedTokens: d.estimatedTokens,
    // Only reached when the server estimate failed. The server always prices
    // its own answer (in pounds, at the customer's markup, with the team's
    // budget left); this approximate figure is a rate-card cost, and the page
    // cannot know the markup, so it carries the default one and no budget.
    estimatedCost: d.estimatedCost * (1 + FALLBACK_MARKUP_PCT / 100),
    remainingBudget: null,
    humanComparisonText: d.humanComparisonText,
    humanCostGBP: d.humanCodingCost,
    humanDays: d.humanCodingWeeksLow * 5,
    // Nor can it read the customer's unit: credits, at the default £1 a
    // credit, so a non-Manager never reads £ here (no-£ rule).
    moneyUnit: { display_unit: 'credits', credit_value_gbp: 1 },
    isFallback: true,
  }
}

export interface UseThemeAIEstimateParams {
  /** Query stays disabled until the caller actually wants a number (e.g. the cost-confirmation dialog opened). */
  enabled: boolean
  /** Omit to let the hook choose: responses without codes when some remain after a run, else everything. */
  responseScope?: ThemeAIResponseScope
  includeExplanations?: boolean
  includeSentiment?: boolean
  includeEmotion?: boolean
  /** Used only by the client-side fallback formula. */
  themeCount?: number
  /** Used only by the client-side fallback formula; defaults to the task's pool_size. */
  responseCount?: number
  avgWordsPerResponse?: number
}

interface UseThemeAIRunsOptions {
  customer: string
  job: string
  taskId?: string
  task?: ThemeAITask
  /** Run number to fetch results for (Test/Results stages). */
  resultsRunNumber?: number
  estimate?: UseThemeAIEstimateParams
}

// Durable preview and production-run controller for the ThemeAI task page.
export const useThemeAIRuns = ({
  customer,
  job,
  taskId,
  task,
  resultsRunNumber,
  estimate,
}: UseThemeAIRunsOptions) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const baseUrl = `${API_BASE_URL}/themeai/${customer}/${job}`
  const taskUrl = `${baseUrl}/tasks/${taskId}`
  const runsQueryKey = ['themeai_runs', { customer, job, taskId }]

  const runsQuery = useQuery({
    queryKey: runsQueryKey,
    enabled: Boolean(customer && job && taskId && account.user),
    queryFn: async () => {
      const res = await axios.get<{ runs: ThemeAIRun[] }>(`${taskUrl}/runs`)
      return res.data.runs
    },
    // While a run is still pending/running, poll so the table's status, cost
    // and token totals update once the dispatcher finalises it.
    // Every 2s while a run codes: a 120-response run takes ~20s, and a 4s
    // poll missed most of its steps (Adam, 23 Sep).
    refetchInterval: (query) =>
      (query.state.data || []).some(isRunInFlight) ? 2000 : false,
  })

  // Default the results view to the latest run when the caller hasn't picked
  // one explicitly — otherwise the newest run's breakdown never auto-loads.
  const effectiveResultsRun = resolveResultsRunNumber(resultsRunNumber, runsQuery.data)
  const effectiveResultsRunRecord = runsQuery.data?.find((run) => run.run_number === effectiveResultsRun)
  const resultsQueryKey = ['themeai_run_results', { customer, job, taskId, effectiveResultsRun }]

  const currentRuns = (runsQuery.data || []).filter((run) => isThemeAIRunCurrent(task, run))
  const latestCurrentTestRun = resolveLatestCurrentRun(runsQuery.data, task, true)
  const latestCurrentRun = resolveLatestCurrentRun(runsQuery.data, task, false)
  const pendingRun = resolveNeverStartedPendingRun(runsQuery.data, task)
  // Status is in the key so a result read while the preview was still coding
  // is never served as the finished answer.
  const testResultsQueryKey = [
    'themeai_test_run_results',
    { customer, job, taskId, runNumber: latestCurrentTestRun?.run_number, status: latestCurrentTestRun?.status },
  ]

  // Every mutation below that changes run state invalidates through this one
  // helper and RETURNS the promise it makes — TanStack v5 awaits whatever
  // onSuccess returns before flipping isPending back to false, so a caller
  // that awaits mutateAsync() sees post-invalidation state instead of a
  // flash of "stage not available yet" from stale cached data.
  const invalidateAll = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: runsQueryKey }),
    queryClient.invalidateQueries({ queryKey: ['themeai_run_results', { customer, job, taskId }], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['themeai_test_run_results', { customer, job, taskId }], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['themeai_run_records', { customer, job, taskId }], exact: false }),
  ])

  const createRunMutation = useMutation({
    mutationFn: async (payload: CreateThemeAIRunRequest) => {
      const res = await axios.post<ThemeAIRun>(`${taskUrl}/runs`, payload)
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // POST /tasks/:id/runs/:runNumber/execute — creation only records the stub;
  // this dispatches coding batches. 409 means the task's data changed since
  // the run was created (run.data_revision != task.data_revision, per the API
  // contract) — the stub is stale and should be discarded, not retried as-is.
  const executeRunMutation = useMutation({
    mutationFn: async (runNumber: number) => {
      const res = await axios.post<{ run: ThemeAIRun }>(`${taskUrl}/runs/${runNumber}/execute`)
      return res.data.run
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        snackbar.error('The task\'s data changed since this run was created — discard it and start a new run.')
        return
      }
      snackbar.error(extractErrorMessage(error))
    },
  })

  const createTestRunMutation = useMutation({
    mutationFn: async (payload: CreateThemeAITestRunRequest): Promise<CreateThemeAITestRunResult> => {
      try {
        const res = await axios.post<ThemeAITestRunResponse>(`${taskUrl}/runs/test`, payload)
        return { status: 'completed', ...res.data }
      } catch (error) {
        // A 504 only means "still coding" when the server says which run it
        // started; without one (a proxy timeout before the run was created)
        // it is a failure, or the stage would wait forever.
        if (isThemeAITestRunTimeout(error) && axios.isAxiosError(error) && (error.response?.data as { run?: unknown } | undefined)?.run) {
          return { status: 'pending' }
        }
        throw error
      }
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // DELETE /tasks/:id/runs/:runNumber — only a pending, never-started run.
  const discardRunMutation = useMutation({
    mutationFn: async (runNumber: number) => {
      await axios.delete(`${taskUrl}/runs/${runNumber}`)
      return runNumber
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // -- POST /tasks/:id/estimate -- keyed on the task's current context so it
  // never quietly re-estimates against the wrong data revision or frame.
  const dataRevision = task?.data_revision
  const codeFrameId = task?.code_frame_id
  const codeFrameVersion = task?.code_frame_version
  // A finished run changes what "new responses only" covers, so the number of
  // completed full runs is part of every estimate key.
  const completedRunCount = (runsQuery.data || [])
    .filter((run) => !run.is_test && run.status === 'completed' && run.data_revision === task?.data_revision).length
  // How many responses have no codes yet: the "new only" scope's count, for
  // the re-run choice. Same endpoint, no model call.
  const uncodedCountQuery = useQuery({
    queryKey: ['themeai_uncoded_count', { customer, job, taskId, dataRevision, codeFrameId, codeFrameVersion, completedRunCount }],
    enabled: Boolean(customer && job && taskId && account.user && estimate?.enabled && completedRunCount > 0),
    queryFn: async () => {
      const res = await axios.post<ThemeAIEstimateResponse>(`${taskUrl}/estimate`, {
        response_scope: 'new_only',
        code_frame_id: codeFrameId,
        code_frame_version: codeFrameVersion,
      })
      return res.data.response_count
    },
  })

  // The scope to estimate and run: the caller's choice, else "responses
  // without codes" when a run has already coded this data and some remain,
  // else everything.
  const estimateScope: ThemeAIResponseScope = estimate?.responseScope
    ?? (completedRunCount > 0 && (uncodedCountQuery.data ?? 0) > 0 ? 'new_only' : 'full_refresh')
  const estimateQueryKey = ['themeai_estimate', {
    customer, job, taskId, dataRevision, codeFrameId, codeFrameVersion, completedRunCount,
    responseScope: estimateScope,
    includeExplanations: estimate?.includeExplanations,
    includeSentiment: estimate?.includeSentiment,
    includeEmotion: estimate?.includeEmotion,
  }]
  const estimateQuery = useQuery({
    queryKey: estimateQueryKey,
    enabled: Boolean(customer && job && taskId && account.user && estimate?.enabled),
    queryFn: async (): Promise<ThemeAIEstimateQueryResult> => {
      try {
        const res = await axios.post<ThemeAIEstimateResponse>(`${taskUrl}/estimate`, {
          response_scope: estimateScope,
          code_frame_id: codeFrameId,
          code_frame_version: codeFrameVersion,
          include_explanations: estimate?.includeExplanations,
          include_sentiment: estimate?.includeSentiment,
          include_emotion: estimate?.includeEmotion,
        })
        return { isFallback: false, data: res.data }
      } catch (error) {
        if (!axios.isAxiosError(error)) throw error
        return {
          isFallback: true,
          data: estimateThemeAICost({
            responseCount: estimate?.responseCount ?? task?.pool_size ?? 0,
            themeCount: estimate?.themeCount ?? 0,
            includeExplanations: Boolean(estimate?.includeExplanations),
            includeSentiment: estimate?.includeSentiment,
            includeEmotion: estimate?.includeEmotion,
            avgWordsPerResponse: estimate?.avgWordsPerResponse,
          }),
        }
      }
    },
  })

  // -- GET /tasks/:id/runs/:runNumber/results --
  const resultsQuery = useQuery({
    queryKey: resultsQueryKey,
    enabled: Boolean(
      customer
      && job
      && taskId
      && effectiveResultsRun
      && effectiveResultsRunRecord?.status === 'completed'
      && account.user,
    ),
    queryFn: async (): Promise<{ available: true; results: ThemeAIRunResultsResponse } | { available: false }> => {
      try {
        const res = await axios.get<ThemeAIRunResultsResponse>(`${taskUrl}/runs/${effectiveResultsRun}/results`)
        return { available: true, results: res.data }
      } catch (error) {
        if (isNotAvailableYet(error)) return { available: false }
        throw error
      }
    },
  })

  const testResultsQuery = useQuery({
    queryKey: testResultsQueryKey,
    enabled: Boolean(customer && job && taskId && latestCurrentTestRun?.status === 'completed' && account.user),
    queryFn: async () => {
      const res = await axios.get<ThemeAIRunResultsResponse>(
        `${taskUrl}/runs/${latestCurrentTestRun?.run_number}/results`,
      )
      return res.data
    },
  })

  const mutationTestRun = createTestRunMutation.data?.status === 'completed' ? createTestRunMutation.data.run : undefined
  const testRunPending = createTestRunMutation.data?.status === 'pending'
  // Whether the test just run avoided the responses the agent read. Known
  // only for a test started in this visit.
  const testFreshSample = createTestRunMutation.data?.status === 'completed'
    ? createTestRunMutation.data.fresh_sample
    : undefined
  const testResults = mutationTestRun?.run_number === latestCurrentTestRun?.run_number
    ? createTestRunMutation.data?.status === 'completed'
      ? createTestRunMutation.data.results
      : undefined
    : latestCurrentTestRun?.status === 'completed'
      ? testResultsQuery.data
      : undefined

  // -- GET /tasks/:id/runs/:runNumber/csv --
  const buildCsvDownloadUrl = (runNumber: number): string =>
    `${taskUrl}/runs/${runNumber}/csv?access_token=${account.user?.token || ''}`

  const downloadRunCsv = (runNumber: number) => {
    window.open(buildCsvDownloadUrl(runNumber), '_blank')
  }

  // -- POST /tasks/:id/corrections (CSV round-trip, spec §15.1) --
  const uploadCorrectionsMutation = useMutation({
    mutationFn: async (file: File): Promise<{ available: true } | { available: false }> => {
      const form = new FormData()
      form.append('file', file)
      try {
        await axios.post(`${taskUrl}/corrections`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        return { available: true }
      } catch (error) {
        if (isNotAvailableYet(error)) return { available: false }
        throw error
      }
    },
    onSuccess: (result) => {
      if (result.available) {
        snackbar.success('Corrections uploaded — the task will reflect your edits.')
        return invalidateAll()
      }
      snackbar.info('Corrections upload is not available yet — this lands with the results backend.')
      return undefined
    },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // -- Wave 2a (#956): POST /tasks/:id/write-variable --
  // Task-level write-back: publishes every currently-coded record for the
  // task (not just one run's coverage). A 409 (variable name already exists)
  // is surfaced as a distinct 'conflict' result rather than a snackbar error
  // so the caller can drive the double-confirm overwrite flow (spec §12.4).
  const writeVariableMutation = useMutation({
    mutationFn: async (payload: WriteThemeAITaskVariableRequest): Promise<WriteThemeAIVariableResult> => {
      try {
        const res = await axios.post<WriteThemeAIVariableResponse>(`${taskUrl}/write-variable`, payload)
        return { status: 'ok', data: res.data }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          return { status: 'conflict' }
        }
        throw error
      }
    },
    onSuccess: (result) => {
      if (result.status === 'ok') return invalidateAll()
      return undefined
    },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // -- Handlers (Page -> Hook -> Component: components get data + these callbacks only) --

  const handleCreateTestRun = (
    sampleSize: number,
    options?: { includeExplanations?: boolean; includeSentiment?: boolean; includeEmotion?: boolean; agentSampleSize?: number },
  ) => {
    // Clear a previous run's error/pending state before firing a new attempt.
    createTestRunMutation.reset()
    return createTestRunMutation.mutateAsync({
      sample_size: sampleSize,
      code_frame_id: task?.code_frame_id,
      code_frame_version: task?.code_frame_version,
      include_explanations: options?.includeExplanations,
      include_sentiment: options?.includeSentiment,
      include_emotion: options?.includeEmotion,
      agent_sample_size: options?.agentSampleSize,
    })
  }

  const handleStartRun = async (payload: CreateThemeAIRunRequest) => {
    const created = await createRunMutation.mutateAsync(payload)
    return executeRunMutation.mutateAsync(created.run_number)
  }

  const handleResumePendingRun = () => {
    if (!pendingRun) return Promise.resolve(undefined)
    return executeRunMutation.mutateAsync(pendingRun.run_number)
  }

  const handleDiscardPendingRun = () => {
    if (!pendingRun) return Promise.resolve(undefined)
    return discardRunMutation.mutateAsync(pendingRun.run_number)
  }

  const uploadCorrections = (file: File) => uploadCorrectionsMutation.mutateAsync(file)
  const handleWriteVariable = (payload: WriteThemeAITaskVariableRequest) => writeVariableMutation.mutateAsync(payload)

  return {
    runs: runsQuery.data || [],
    currentRuns,
    runsLoading: runsQuery.isLoading,
    runsError: runsQuery.error,
    refetchRuns: runsQuery.refetch,

    latestCurrentTestRun,
    latestCurrentRun,
    pendingRun,

    handleStartRun,
    startRunLoading: createRunMutation.isPending || executeRunMutation.isPending,
    handleResumePendingRun,
    resumePendingRunLoading: executeRunMutation.isPending,
    handleDiscardPendingRun,
    discardPendingRunLoading: discardRunMutation.isPending,

    handleCreateTestRun,
    createTestRunLoading: createTestRunMutation.isPending,
    createTestRunError: createTestRunMutation.isError
      ? extractErrorMessage(createTestRunMutation.error)
      : undefined,
    testRunPending,
    testFreshSample,
    testResults,
    testResultsLoading: testResultsQuery.isLoading,

    estimate: estimateQuery.data ? normalizeThemeAIEstimate(estimateQuery.data) : undefined,
    estimateLoading: estimateQuery.isLoading,
    estimateError: estimateQuery.error,
    uncodedCount: uncodedCountQuery.data,
    estimateScope,

    results: resultsQuery.data,
    resultsLoading: resultsQuery.isLoading,
    resultsError: resultsQuery.error,
    resultsRunNumber: effectiveResultsRun,
    resultsRun: effectiveResultsRunRecord,

    buildCsvDownloadUrl,
    downloadRunCsv,

    uploadCorrections,
    uploadCorrectionsLoading: uploadCorrectionsMutation.isPending,

    handleWriteVariable,
    writeVariableLoading: writeVariableMutation.isPending,
  }
}

export type ThemeAIRunsController = ReturnType<typeof useThemeAIRuns>

// -- GET /tasks/:id/runs/:runNumber/records -- the drill-through query: a bar
// click filters this list down to records that matched a theme, or carry a
// sentiment or emotion code (#1523).
export interface UseThemeAIRunRecordsOptions {
  theme?: string
  sentiment?: string
  emotion?: string
  limit?: number
  offset?: number
}

export const useThemeAIRunRecords = (
  customer: string,
  job: string,
  taskId: string | undefined,
  runNumber: number | undefined,
  options: UseThemeAIRunRecordsOptions = {},
) => {
  const account = useAccount()
  const baseUrl = `${API_BASE_URL}/themeai/${customer}/${job}`
  const { theme, sentiment, emotion, limit = 50, offset = 0 } = options
  const queryKey = ['themeai_run_records', { customer, job, taskId, runNumber, theme, sentiment, emotion, limit, offset }]

  const query = useQuery({
    queryKey,
    enabled: Boolean(customer && job && taskId && runNumber && account.user),
    queryFn: async () => {
      const res = await axios.get<ThemeAIRunRecordsPage>(
        `${baseUrl}/tasks/${taskId}/runs/${runNumber}/records`,
        { params: { limit, offset, theme: theme || undefined, sentiment: sentiment || undefined, emotion: emotion || undefined } },
      )
      return res.data
    },
    // Keep the previous page visible while paging/re-filtering by theme so
    // the table doesn't flash empty between requests.
    placeholderData: keepPreviousData,
  })

  return {
    records: query.data?.records || [],
    recordsTotal: query.data?.total || 0,
    recordsLoading: query.isLoading,
    recordsFetching: query.isFetching,
    recordsError: query.error,
    refetchRecords: query.refetch,
  }
}

export default useThemeAIRuns
